<template>
  <bm-dialog
    ref="bmDialogRef"
    :visible="value"
    :label="messages.crm_log_communication"
    :width="1000"
    @cancel="cancel"
    @accept="validateAndAccept"
  >
    <communication-log-control v-model="communicationLogEntry" :cache="cache">
      <template v-if="entityType === EnumBmEntityType.outlet" #prepend>
        <v-row>
          <v-col cols="12">
            <bm-dropdown
              v-model="communicationLogEntry.entityId"
              :t="keys.outlet"
              :items="cache.outlets.all"
              :item-text="(item) => [item.displayName, item.code]"
              :item-value="(item) => item.id"
              :rules="[$ruleSet.required()]"
            />
          </v-col>
        </v-row>
      </template>

      <template v-if="showCompleteTaskCheckbox" #append>
        <v-row>
          <v-col cols="12">
            <bm-checkbox v-model="completeTask" :t="keys.tasks_complete_task" />
          </v-col>
        </v-row>
      </template>
    </communication-log-control>

    <template #controls-confirm="{ isDesktopView }">
      <bm-btn
        :t="keys.save"
        :block="isDesktopView"
        :loading="saving"
        @click="validateAndAccept"
      />
    </template>
  </bm-dialog>
</template>

<script lang="ts">
import BmDialog from "@/components/dialogs/BmDialog.vue";
import type { BmTask } from "@/app/services/generated/bmtaskService";
import type { PropType } from "vue";
import { onMounted, defineComponent, ref, toRef, watchEffect } from "vue";
import {
  addCommunicationLogEntry,
  CommunicationLogEntry,
  createCommunicationLogEntryWithDefaults,
} from "@/components/pages/outlet/crm/communication_logs/communicationLog";
import type { BmFormInterface } from "@/components/controls/BmFormTypes";
import { useReactiveDialogSetup } from "@/components/mixins/dialogs";
import { EnumBmEntityType } from "@/app/services/generated/enums/enumBmEntityType.generated";
import type Guid from "@/app/types/common/guid";
import CommunicationLogControl from "@/components/pages/outlet/crm/communication_logs/CommunicationLogControl.vue";
import BmDropdown from "@/components/controls/input/BmDropdown.vue";
import apiCache from "@/app/caching/apiCache";
import OutletSummariesCache from "@/app/caching/caches/outletSummariesCache";
import messages, { keys } from "@/plugins/i18n";
import useLoadingState from "@/app/useLoadingState";
import { makeDeepRef } from "@/components/composables/reactivityTypes";
import CommunicationTypesCache from "@/app/caching/caches/communicationTypesCache";
import CommunicationSubjectsCache from "@/app/caching/caches/communicationSubjectsCache";

export interface LogCommunicationDialogPayload {
  completeTask: boolean;
}

export default defineComponent({
  name: "LogCommunicationDialog",
  components: {
    BmDropdown,
    CommunicationLogControl,
    BmDialog,
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    task: {
      type: Object as PropType<BmTask>,
      required: false,
    },
    showCompleteTask: {
      type: Boolean,
      required: false,
    },
    entityType: {
      type: Object as PropType<EnumBmEntityType>,
      required: false,
      default: () => EnumBmEntityType.outlet,
    },
    entityId: {
      type: Object as PropType<Guid>,
      required: false,
    },
  },
  data: () => ({ EnumBmEntityType }),
  setup(props, context) {
    const bmDialogRef = ref<BmFormInterface | null>(null);
    const completeTask = ref(true);
    const showCompleteTaskCheckbox = ref(true);

    const communicationLogEntry = makeDeepRef(
      new CommunicationLogEntry(
        props.entityId ?? props.task?.associatedBmEntityId ?? null
      )
    );

    const cache = apiCache.buildCacheSet({
      outlets: OutletSummariesCache,
      communicationTypes: CommunicationTypesCache,
      communicationSubjects: CommunicationSubjectsCache,
    });

    const { accept, cancel } = useReactiveDialogSetup(
      toRef(props, "value"),
      context
    );

    const [saving, validateAndAccept] = useLoadingState(async () => {
      if (!bmDialogRef.value?.validate()) {
        return;
      }

      await addCommunicationLogEntry({
        communicationLogEntry: communicationLogEntry.value,
        entityType: props.entityType,
      });

      const payload: LogCommunicationDialogPayload = {
        completeTask: completeTask.value,
      };

      accept(payload);
    });

    onMounted(async () => {
      await cache.update();

      communicationLogEntry.value = createCommunicationLogEntryWithDefaults({
        communicationSubjects: cache.communicationSubjects.all,
        communicationTypes: cache.communicationTypes.all,
        entityId: props.entityId ?? props.task?.associatedBmEntityId ?? null,
      });
    });

    watchEffect(() => {
      // if dropdown value changes from original value, hide checkbox and
      // don't allow task competion
      if (
        !props.showCompleteTask ||
        (props.task?.associatedBmEntityId &&
          communicationLogEntry.value.entityId &&
          !communicationLogEntry.value.entityId.equals(
            props.task.associatedBmEntityId
          ))
      ) {
        completeTask.value = false;
        showCompleteTaskCheckbox.value = false;
      } else {
        showCompleteTaskCheckbox.value = true;
      }
    });

    return {
      bmDialogRef,
      completeTask,
      validateAndAccept,
      cancel,
      showCompleteTaskCheckbox,
      communicationLogEntry,
      cache,
      saving,
      messages,
      keys,
    };
  },
});
</script>
